import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,  faWandMagicSparkles, faFilePdf, faEye, faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../AuthContext';
import { citationsApi, saveBlob } from './Citationsapi';
import { Loader, ErrorBox, Empty } from '../ecourt/EcourtUi';
import { UsageBadge, PoweredBy, fmtDate, fmtTs, Cost } from './Citationui';

/** A case in the Cites / Cited by list, with an optional "Quick look" (Document Metainfo price). */
function CitedCase({ userId, item, prices, onBalance }) {
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (meta || (error && error.code === 'NOT_FOUND')) return;
    setLoading(true);
    setError(null);
    try {
      const r = await citationsApi.meta(userId, item.tid);
      setMeta(r);
      if (onBalance && r.balance) onBalance(r.balance);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  // Acts and Sections have no Quick look on Indian Kanoon. Lists inside a judgment only carry
  // tid + title, so also tell them apart by title: "Section 4 in The X Act, 1961", "The X Act, 1961".
  const title = item.title || '';
  const isJudgment = /\sv(s|\.)?\.?\s/i.test(title) || /\son\s\d{1,2}\s\w+,\s*\d{4}\s*$/i.test(title);
  const statuteWord = /\b(Act|Code|Rules?|Regulations?|Ordinance|Constitution|Order)\b/i;
  const isStatute = /-\s*(Act|Section|Rule|Article|Order)s?\s*$/i.test(item.court || '')
    || (!isJudgment && (/^(Section|Article|Rule|Order)\s/i.test(title) || statuteWord.test(title)));
  const statuteLabel = /^(Section|Article|Rule|Order)\s/i.test(title)
    ? title.split(/\s/)[0]
    : ((item.court || '').match(/(Act|Section|Rule|Article|Order)/i) || ['Act'])[0];

  return (
    <article className="ec-result cite-result">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <Link to={`/citations/doc/${item.tid}`} className="ec-link-plain ec-minw0">
          <span className="ec-result-title">{item.title || 'Untitled judgment'}</span>
          {(item.court || item.date) && (
            <span className="d-block small ec-muted">{[item.court, fmtDate(item.date)].filter(Boolean).join(' · ')}</span>
          )}
        </Link>
        {isStatute ? (
          <span className="ec-badge ec-badge-grey">{statuteLabel}</span>
        ) : (
        <button type="button" className="btn btn-sm btn-ec-outline" onClick={toggle} aria-expanded={open}>
          <FontAwesomeIcon icon={open ? faChevronUp : faEye} /> {open ? 'Hide' : 'Quick look'}
          {!meta && !open && !(error && error.code === 'NOT_FOUND') && <Cost prices={prices} kind="docmeta" />}
        </button>
        )}
      </div>
      {open && (
        <div className="cite-meta mt-2">
          {loading && <div className="small ec-muted">Loading details…</div>}
          {error && (error.code === 'NOT_FOUND' ? (
            <div className="small ec-muted">
              Quick look is not available for this one on Indian Kanoon (nothing was charged).{' '}
              <Link to={`/citations/doc/${item.tid}`} className="ec-link">Open it</Link> to read the full text.
            </div>
          ) : <div className="small ec-field-error" role="alert">{error.message}</div>)}
          {meta && (
            <dl className="cite-meta-grid mb-0">
              {meta.details.length === 0 && <div className="small ec-muted">No extra details on Indian Kanoon.</div>}
              {meta.details.map((d) => (
                <div key={d.label}>
                  <dt>{d.label}</dt>
                  <dd>{d.label === 'Date' ? fmtDate(d.value) : d.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      )}
    </article>
  );
}

/**
 * Loads the full "Cited by" / "Cites" list, 10 per page, through Indian Kanoon search
 * (the same search its own "Cited by N" link runs). Each page uses the Search price once; saved pages are free.
 */
function CiteListLoader({ userId, tid, type, total, shown, prices, onBalance }) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => { setItems([]); setPage(0); setDone(false); setError(null); }, [tid, type]);

  const loadNext = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await citationsApi.citeList(userId, tid, type, page);
      const seen = new Set(items.map((x) => x.tid));
      const fresh = r.results.filter((x) => !seen.has(x.tid) && x.tid !== Number(tid));
      setItems((prev) => [...prev, ...fresh]);
      setPage((p) => p + 1);
      if (r.results.length < 10) setDone(true);
      if (onBalance && r.balance) onBalance(r.balance);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  const label = type === 'citedby' ? 'cases that cite this judgment' : 'cases this judgment cites';
  const started = page > 0;

  return (
    <div className="mt-2">
      {items.map((c) => (
        <CitedCase key={`l-${c.tid}`} userId={userId} item={c} prices={prices} onBalance={onBalance} />
      ))}
      {error && <div className="small ec-field-error mb-2" role="alert">{error.message}</div>}
      {!done && (
        <button type="button" className="btn btn-ec-outline" onClick={loadNext} disabled={loading}>
          {loading ? 'Loading…' : started
            ? 'Load more'
            : `${shown ? 'Load all' : 'Load the'} ${Number(total).toLocaleString('en-IN')} ${label}`}
          {!loading && <Cost prices={prices} kind="search" />}
          {!loading && <span className="small ec-muted">per 10</span>}
        </button>
      )}
      {done && started && items.length === 0 && <div className="small ec-muted">Indian Kanoon returned no more cases.</div>}
    </div>
  );
}

export default function CitationDoc() {
  const { user } = useAuth();
  const userid = user?.id;
  const { tid } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('text');
  const [copyBusy, setCopyBusy] = useState(false);
  const [copyError, setCopyError] = useState(null);

  const load = useCallback(async () => {
    if (!userid) return;
    setLoading(true);
    setError(null);
    try {
      const d = await citationsApi.doc(userid, tid);
      setDoc(d);
      setBalance(d.balance);
    } catch (e) {
      setError(e);
      setDoc(null);
    } finally {
      setLoading(false);
    }
  }, [userid, tid]);

  useEffect(() => { setTab('text'); setCopyError(null); load(); }, [load]);

  const goBack = () => {
    if (window.history.state && window.history.state.idx > 0) navigate(-1);
    else navigate('/citations');
  };

  // Same hand-off the sidebar's DPDP links use to start a chat with a prompt.
  const askAi = () => {
    const where = [doc.court, fmtDate(doc.date)].filter(Boolean).join(', ');
    const prompt = `Summarise the judgment "${doc.title}"${where ? ` (${where})` : ''}. `
      + 'Give the facts, issues, decision and ratio decidendi, and list the key precedents it relies on.';
    localStorage.setItem('session_id', window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : String(Date.now()));
    navigate('/chat', { state: { dpdpPrompt: prompt, ts: Date.now() } });
  };

  const downloadCourtCopy = async () => {
    setCopyBusy(true);
    setCopyError(null);
    try {
      const { blob, name } = await citationsApi.courtCopy(userid, doc.tid);
      saveBlob(blob, name);
      citationsApi.usage(userid).then(setBalance).catch(() => {});
    } catch (e) {
      setCopyError(e);
    } finally {
      setCopyBusy(false);
    }
  };

  const back = (
    <button type="button" className="ec-back btn btn-link p-0" onClick={goBack}>
      <FontAwesomeIcon icon={faChevronLeft} /> Back
    </button>
  );

  if (loading) return <div className="cite-page">{back}<Loader label="Opening judgment…" /></div>;
  if (error) {
    return (
      <div className="cite-page">
        {back}
        <ErrorBox error={error} onRetry={error.status >= 500 ? load : undefined} />
        {error.code === 'INSUFFICIENT_BALANCE' && (
          <p className="ec-muted">Judgments you have opened before still open for free. <Link to="/Subscription" className="ec-link">Recharge</Link></p>
        )}
      </div>
    );
  }
  if (!doc) return null;

  const prices = balance && balance.prices;
  // Acts / Sections have no court copy on Indian Kanoon.
  const docIsStatute = /-\s*(Act|Section|Rule|Article|Order)s?\s*$/i.test(doc.court || '');
  const citedByCount = Math.max(Number(doc.numCitedBy) || 0, doc.citedBy.length);
  const citesCount = Math.max(Number(doc.numCites) || 0, doc.cites.length);
  const tabs = [
    { id: 'text', label: 'Judgment' },
    { id: 'cites', label: `Cites (${citesCount.toLocaleString('en-IN')})` },
    { id: 'citedby', label: `Cited by (${citedByCount.toLocaleString('en-IN')})` },
  ];
  const list = tab === 'cites' ? doc.cites : doc.citedBy;

  return (
    <div className="cite-page">
      {back}

      <section className="ec-card mb-3">
        <div className="d-flex flex-wrap justify-content-between gap-3">
          <div className="ec-minw0">
            <h1 className="ec-h1 my-1">{doc.title}</h1>
            <div className="ec-muted">{[doc.court, fmtDate(doc.date)].filter(Boolean).join(' · ')}</div>
            <div className="d-flex flex-wrap gap-2 mt-2">
              <span className="ec-badge ec-badge-blue">Cites {citesCount.toLocaleString('en-IN')}</span>
              <span className="ec-badge ec-badge-green">Cited by {citedByCount.toLocaleString('en-IN')}</span>
              {doc.cached && <span className="ec-badge ec-badge-grey">Saved {fmtTs(doc.savedTs)} · free</span>}
              <UsageBadge usage={balance} />
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-start">
            <button type="button" className="btn btn-ec" onClick={askAi}>
              <FontAwesomeIcon icon={faWandMagicSparkles} /> Ask AI about this
            </button>
            {!docIsStatute && (
              <button type="button" className="btn btn-ec-outline" onClick={downloadCourtCopy} disabled={copyBusy || (copyError && copyError.status === 404)}>
                <FontAwesomeIcon icon={faFilePdf} /> {copyBusy ? 'Downloading…' : 'Court copy'}
                {!copyBusy && !(copyError && copyError.status === 404) && <Cost prices={prices} kind="origdoc" />}
              </button>
            )}
            {/* <a className="btn btn-ec-outline" href={`https://indiankanoon.org/doc/${doc.tid}/`} target="_blank" rel="noopener noreferrer">
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> Indian Kanoon
            </a> */}
          </div>
        </div>
        {copyError && (copyError.status === 404
          ? <div className="small ec-muted mt-2" role="status">{copyError.message} Nothing was charged.</div>
          : <div className="small ec-field-error mt-2" role="alert">{copyError.message}</div>)}
      </section>

      <ul className="nav ec-tabs mb-3" role="tablist">
        {tabs.map((t) => (
          <li className="nav-item" key={t.id} role="presentation">
            <button type="button" role="tab" aria-selected={tab === t.id} className={`nav-link ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          </li>
        ))}
      </ul>

      {tab === 'text' ? (
        doc.text ? <div className="ec-order-text cite-text">{doc.text}</div> : <Empty>No text available for this judgment.</Empty>
      ) : (
        (() => {
          const total = tab === 'cites' ? citesCount : citedByCount;
          const type = tab === 'cites' ? 'cites' : 'citedby';
          if (list.length === 0 && total === 0) {
            return <Empty>{tab === 'cites' ? 'This judgment does not cite other cases on Indian Kanoon.' : 'No later cases cite this judgment yet.'}</Empty>;
          }
          return (
            <>
              {list.length > 0 && total > list.length && (
                <p className="small ec-muted">Showing the top {list.length} of {total.toLocaleString('en-IN')}.</p>
              )}
              {list.map((c) => (
                <CitedCase key={c.tid} userId={userid} item={c} prices={prices} onBalance={setBalance} />
              ))}
              {total > list.length && (
                <CiteListLoader key={type} userId={userid} tid={doc.tid} type={type} total={total}
                  shown={list.length > 0} prices={prices} onBalance={setBalance} />
              )}
            </>
          );
        })()
      )}

      <PoweredBy />
    </div>
  );
}